import { useEffect, useId, useState } from 'react';
import {
  ChevronDown,
  Clock,
  Facebook,
  Instagram,
  Mail as MailIcon,
  MapPin,
  Phone,
  type LucideIcon,
} from 'lucide-react';
import { contact, formatFullAddress } from '@/content/et/contact';
import { site } from '@/content/et/site';
import { formatMailto, formatPhoneHref } from '@/lib/format';
import { useMapsCta } from '@/lib/mapsCta';
import { Button } from '@/components/ui/Button';
import { ContactForm } from './ContactForm';
import { ContactMap } from './ContactMap';
import { OpeningHours } from './OpeningHours';
import styles from './ContactSection.module.css';

const FORM_DESKTOP_MQ = '(min-width: 900px)';

function ContactItem({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.row}>
      <span className={styles.itemIconWrap} aria-hidden="true">
        <Icon className={styles.itemIcon} size={18} strokeWidth={1.75} />
      </span>
      <div className={styles.itemBody}>
        <span className={styles.label}>{label}</span>
        <div className={styles.value}>{children}</div>
      </div>
    </div>
  );
}

export function ContactSection() {
  /** Mobile accordion for Saada päring — closed by default; desktop always open */
  const [formOpen, setFormOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const formPanelId = useId();
  const expanded = isDesktop || formOpen;
  const mapsCta = useMapsCta();

  useEffect(() => {
    const mq = window.matchMedia(FORM_DESKTOP_MQ);
    const sync = () => setIsDesktop(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return (
    <section
      id="kontakt"
      className={`section ${styles.section}`}
      aria-labelledby="kontakt-title"
    >
      <div className={styles.mapStage} aria-hidden="true">
        <ContactMap variant="backdrop" />
        <div className={styles.mapScrim} />
        <div className={styles.mapVignette} />
      </div>

      <div className={`container ${styles.content}`}>
        <header className={styles.header}>
          <h2
            id="kontakt-title"
            className={`section__title section__title--tight ${styles.title}`}
          >
            {site.contact.title}
          </h2>
          <p className={styles.lead}>
            Helistage, kirjutage või saatke päring —
            <br className={styles.leadBreak} />
            vastame esimesel võimalusel.
          </p>
        </header>

        <div className={styles.grid}>
          <div className={styles.infoCard}>
            <div className={styles.rows}>
              <ContactItem icon={Phone} label={site.contact.phone}>
                <a
                  href={formatPhoneHref(contact.phoneE164)}
                  className={styles.phoneLink}
                >
                  {contact.phoneDisplay}
                </a>
              </ContactItem>
              <ContactItem icon={MapPin} label={site.contact.address}>
                <p>{formatFullAddress()}</p>
              </ContactItem>
              <ContactItem icon={Clock} label={site.contact.hours}>
                <OpeningHours />
              </ContactItem>
              <ContactItem icon={MailIcon} label={site.contact.email}>
                <a href={formatMailto(contact.email, 'Päring autoremont')}>
                  {contact.email}
                </a>
              </ContactItem>
            </div>

            <div className={styles.socialLinks} aria-label="Sotsiaalmeedia">
              <a
                href={contact.facebookUrl}
                className={styles.socialLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <Facebook size={33} strokeWidth={1.6} aria-hidden />
              </a>
              <a
                href={contact.instagramUrl}
                className={styles.socialLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <Instagram size={33} strokeWidth={1.6} aria-hidden />
              </a>
            </div>

            <div className={styles.actions}>
              <Button
                href={formatPhoneHref(contact.phoneE164)}
                className={styles.callMobile}
                fullWidth
              >
                <Phone size={20} strokeWidth={1.75} aria-hidden />
                {site.cta.call}
              </Button>
              <Button
                href={mapsCta.href}
                variant="primary"
                fullWidth
                target="_blank"
                rel="noopener noreferrer"
                className={styles.mapsBtn}
              >
                <MapPin size={20} strokeWidth={1.75} aria-hidden />
                {mapsCta.label}
              </Button>
            </div>
          </div>

          <div
            className={[
              styles.formPanel,
              expanded ? styles.formPanelOpen : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <button
              type="button"
              className={styles.formToggle}
              aria-expanded={expanded}
              aria-controls={formPanelId}
              onClick={() => {
                if (!isDesktop) setFormOpen((v) => !v);
              }}
              tabIndex={isDesktop ? -1 : 0}
            >
              <h3 className={styles.formTitle}>{site.cta.enquire}</h3>
              <ChevronDown
                className={styles.formChevron}
                size={20}
                strokeWidth={1.75}
                aria-hidden
              />
            </button>
            <div
              id={formPanelId}
              className={styles.formCollapse}
              aria-hidden={!expanded}
            >
              <div className={styles.formCollapseInner}>
                <ContactForm />
              </div>
            </div>
          </div>
        </div>

        <div
          id="kontakt-map-focus"
          className={styles.mapFocusBand}
          aria-hidden="true"
        />
      </div>
    </section>
  );
}
